import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Send, CheckCircle2, AlertTriangle, Link as LinkIcon } from 'lucide-react';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';

/**
 * Fires every step of a campaign at a single email address immediately,
 * ignoring the normal day delays. Follow-ups thread as replies when the
 * campaign has thread_replies=true. Does NOT touch contacts or metrics.
 */
export default function TestSequenceDialog({ open, onOpenChange, campaign, defaultEmail = '' }) {
  const [email, setEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setResult(null); setSending(false); };

  const submit = async () => {
    if (!email || !email.includes('@')) return toast.error('Enter a valid email');
    setSending(true); setResult(null);
    try {
      const r = await api.post(`/campaigns/${campaign.id}/test-sequence`, { to_email: email });
      setResult(r.data);
      if (r.data.ok) toast.success(`Sent ${r.data.results.length} test step(s) to ${email}`);
      else toast.error('Test sequence had failures — see details below', { duration: 8000 });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Test sequence failed'));
    } finally {
      setSending(false);
    }
  };

  const threadOn = campaign?.thread_replies !== false;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="bg-[#111110] border-zinc-800 text-zinc-100 max-w-2xl" data-testid="test-sequence-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Send test sequence</DialogTitle>
        </DialogHeader>
        <div className="text-sm text-zinc-400 -mt-2">
          Fires the entire sequence ({campaign?.steps?.length || 0} step{campaign?.steps?.length === 1 ? '' : 's'})
          at one address <strong className="text-zinc-200">immediately</strong>, ignoring day delays.{' '}
          {threadOn ? (
            <span className="text-[#f5d67a]">Threading is ON — follow-ups will land in the same conversation as "Re: [subject]".</span>
          ) : (
            <span className="text-zinc-500">Threading is OFF — each step lands as a separate email.</span>
          )}
        </div>
        <div className="mt-3 text-xs text-orange-300 border border-orange-900/70 bg-orange-950/40 rounded-md px-3 py-2">
          Test sends do <strong>not</strong> update contacts, events, or campaign stats. Personalization uses placeholder values (Test Recruiter, Acme Corp, …).
        </div>

        {!result && (
          <div className="mt-4 space-y-2">
            <Label htmlFor="test-seq-email" className="text-xs text-zinc-500">Send to</Label>
            <Input
              id="test-seq-email"
              data-testid="test-sequence-email"
              type="email"
              placeholder="you@yourdomain.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0a0a0b] border-zinc-800 text-zinc-100 focus-visible:ring-[#d4a94a]"
            />
          </div>
        )}

        {result && (
          <div className="mt-4 space-y-2 max-h-[45vh] overflow-y-auto">
            {result.results.map((r) => (
              <div key={r.step} className="rounded-md border border-zinc-800 bg-[#0a0a0b] p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-6 h-6 rounded-full bg-[#1a1815] border border-[#3a2f18] text-[#f5d67a] flex items-center justify-center text-xs">{r.step}</div>
                  <div className="text-sm font-medium text-zinc-100">{r.is_initial ? 'Initial email' : `Follow-up ${r.step - 1}`}</div>
                  {r.ok ? (
                    <Badge className="bg-emerald-950 text-emerald-300 border border-emerald-900 text-[10px]"><CheckCircle2 size={10} className="mr-1" />Sent · HTTP {r.graph_status}</Badge>
                  ) : (
                    <Badge className="bg-red-950 text-red-300 border border-red-900 text-[10px]"><AlertTriangle size={10} className="mr-1" />Failed · HTTP {r.graph_status || '—'}</Badge>
                  )}
                  {r.threaded && (
                    <Badge className="bg-[#1a1815] text-[#f5d67a] border border-[#3a2f18] text-[10px]"><LinkIcon size={10} className="mr-1" />Threaded reply</Badge>
                  )}
                  {r.is_initial && r.captured_message_id && (
                    <Badge className="bg-zinc-900 text-zinc-300 border border-zinc-800 text-[10px]">msg id captured</Badge>
                  )}
                  {r.is_initial && r.thread_capture_failed && (
                    <Badge className="bg-orange-950 text-orange-300 border border-orange-900 text-[10px]">msg id NOT captured — follow-ups will send unthreaded</Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-400 mt-1.5 truncate"><span className="text-zinc-500">Subject:</span> {r.effective_subject || '(empty)'}</div>
                {r.conversation_id && (
                  <div className="text-[10px] text-zinc-600 mt-0.5 font-mono truncate">conv: {r.conversation_id}</div>
                )}
                {r.reply_to_message_id && (
                  <div className="text-[10px] text-zinc-600 mt-0.5 font-mono truncate">replying to: {r.reply_to_message_id.slice(0, 40)}…</div>
                )}
                {r.diagnostics && Object.keys(r.diagnostics).length > 0 && (
                  <details className="mt-2 group">
                    <summary className="text-[10px] text-zinc-500 hover:text-[#f5d67a] cursor-pointer select-none">Show Graph API stages</summary>
                    <pre className="text-[10px] text-zinc-400 mt-1 whitespace-pre-wrap break-words font-mono bg-black/40 border border-zinc-800 rounded p-2 max-h-40 overflow-y-auto">{JSON.stringify(r.diagnostics, null, 2)}</pre>
                  </details>
                )}
                {r.graph_body && !r.ok && (
                  <div className="text-xs text-red-300 mt-1 whitespace-pre-wrap break-words">{r.graph_body}</div>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-4">
          {result ? (
            <>
              <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={() => { reset(); }}>Send another</Button>
              <Button className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium" onClick={() => onOpenChange(false)}>Close</Button>
            </>
          ) : (
            <>
              <Button variant="outline" className="border-zinc-800 text-zinc-200 hover:bg-zinc-900" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                className="bg-[#d4a94a] hover:bg-[#e5bb5c] text-black font-medium"
                onClick={submit}
                disabled={sending}
                data-testid="test-sequence-send-btn"
              >
                <Send size={14} className="mr-1" />{sending ? 'Sending…' : `Send ${campaign?.steps?.length || 0} step(s)`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
